import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import '../components/LoadingSpinner.css';
import './EmailVerification.css';

const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // 从URL参数中获取验证token
        const token = searchParams.get('token');
        const email = searchParams.get('email');

        if (!token) {
          setVerificationStatus('error');
          setErrorMessage('验证链接无效，请检查邮件中的链接是否完整');
          return;
        }

        // 调用后端API验证邮箱
        await authAPI.verifyEmail(token);

        setVerificationStatus('success');

        // 延迟跳转到登录页面
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (error: any) {
        console.error('Email verification error:', error);
        setVerificationStatus('error');
        if (error.response?.data?.message) {
          setErrorMessage(error.response.data.message);
        } else {
          setErrorMessage('网络错误，请检查网络连接后重试');
        }
      } finally {
        setIsVerifying(false);
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  const handleResendVerification = async () => {
    try {
      const email = searchParams.get('email');
      if (!email) {
        navigate('/register');
        return;
      }

      await authAPI.resendVerification(email);

      // 验证邮件发送成功，可以在这里添加成功的视觉反馈
      const button = document.querySelector('.secondary-button') as HTMLButtonElement;
      if (button) {
        button.textContent = '验证邮件已发送';
        button.disabled = true;
        setTimeout(() => {
          button.textContent = '重新发送验证邮件';
          button.disabled = false;
        }, 5000);
      }
    } catch (error: any) {
      console.error('Resend verification error:', error);
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage('网络错误，请稍后重试');
      }
    }
  };

  const renderContent = () => {
    switch (verificationStatus) {
      case 'loading':
        return (
          <div className="verification-loading">
            <div className="loading-spinner"></div>
            <h2>正在验证邮箱...</h2>
            <p>请稍候，我们正在验证您的邮箱地址</p>
          </div>
        );

      case 'success':
        return (
          <div className="verification-success">
            <div className="success-icon">✓</div>
            <h2>邮箱验证成功！</h2>
            <p>您的邮箱已成功验证，即将跳转到登录页面...</p>
            <div className="success-actions">
              <button
                className="primary-button"
                onClick={() => navigate('/login')}
              >
                立即登录
              </button>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="verification-error">
            <div className="error-icon">✕</div>
            <h2>邮箱验证失败</h2>
            <p className="error-message">{errorMessage}</p>
            <div className="error-actions">
              <button
                className="secondary-button"
                onClick={handleResendVerification}
              >
                重新发送验证邮件
              </button>
              <button
                className="primary-button"
                onClick={() => navigate('/login')}
              >
                返回登录
              </button>
            </div>
            <div className="help-links">
              <p>如果问题持续存在，您可以：</p>
              <ul>
                <li>检查垃圾邮件文件夹</li>
                <li>确认邮件中的链接是否完整</li>
                <li>联系客服获取帮助</li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="verification-container">
      <div className="verification-card">
        <div className="verification-header">
          <h1>邮箱验证</h1>
        </div>

        {renderContent()}
      </div>
    </div>
  );
};

export default EmailVerification;